import parseLocation from 'client/utils/parse-location';
import { useWillUnmount } from 'common/hooks/use-will-unmount';
import React, { useRef } from 'react';
import type { BBCommonProps } from 'types/story';
import { TRANSITION_TYPE } from 'common/constants';
import usePrevious from 'common/hooks/use-previous';
import { useClientSelector } from 'client/reducers';
import { selectCardId } from 'client/reducers/card/selectors';
import { selectStoryCardSettings } from 'client/reducers/story/selectors';
import { CardRendererContext } from 'client/components/common/StoryCard/CardRenderer/context';
import { getExposeParams, parseExposeDuration } from 'client/components/common/BuildingBlocks/utils/common';

export const useCardId = () => {
	const cardIdFromState = useClientSelector(selectCardId); // current story card
	const cardIdFromCtx = React.useContext(CardRendererContext)?.card._id; // card that renders current component

	// fallback in case of component is placed in story elements or card is not being mounted yet
	return cardIdFromCtx || cardIdFromState || parseLocation.getCardId() || undefined;
};

type UseExposeParamsProps = {
	exposeEffect: ValuesType<typeof TRANSITION_TYPE> | undefined;
	bbType: BBCommonProps['type'];
	in: boolean | undefined;
};

export const useExposeParams = ({ exposeEffect, bbType, in: inProp }: UseExposeParamsProps) => {
	const cardId = useCardId();
	const exposeDuration = useClientSelector(state => selectStoryCardSettings(state, cardId))?.expose?.duration ?? '';

	return React.useMemo(
		() =>
			getExposeParams({
				exposeEffect,
				exposeDuration: parseExposeDuration(exposeDuration),
				bbType,
				in: inProp,
			}),
		[exposeEffect, exposeDuration, bbType, inProp]
	);
};

interface UseTransitionEndOptions {
	onTimeoutStart: () => void;
	onTimeoutEnd: (params: { abort: () => void }) => void;
}

export const useTransitionEndTimeout = (props: BBCommonProps, options?: UseTransitionEndOptions) => {
	const transitionTimeout = React.useRef<number | undefined>(undefined);
	const prevProps = usePrevious(props);
	const currPropsRef = useRef(props);
	const optionsRef = useRef(options);
	const isAbortedRef = useRef(false);

	React.useEffect(() => {
		currPropsRef.current = props;
	}, [props]);

	const { duration, effect, delay } = useExposeParams({
		exposeEffect: props.uiConfig.expose?.effect,
		bbType: props.type,
		in: props.in,
	});

	const shouldTransitionEnd = !!effect && props.in !== undefined && props.in !== prevProps?.in;
	const transitionEndDelay = effect ? duration + delay : 0;

	React.useEffect(() => {
		if (shouldTransitionEnd) {
			isAbortedRef.current = false;
			optionsRef.current?.onTimeoutStart();
			clearTimeout(transitionTimeout.current);
			transitionTimeout.current = window.setTimeout(
				() => {
					const abort = () => {
						isAbortedRef.current = true;
					};

					optionsRef.current?.onTimeoutEnd({ abort });

					if (isAbortedRef.current) {
						return;
					}

					currPropsRef.current.onTransitionEnd({
						...currPropsRef.current,
						in: props.in,
					});
				},
				Number.isFinite(transitionEndDelay) ? transitionEndDelay : 0
			);
		}
	}, [shouldTransitionEnd, transitionEndDelay, props.in]);

	useWillUnmount(() => {
		clearTimeout(transitionTimeout.current);
	});

	return null;
};
